import { Avatar, Container, Group, Loader, Paper, Text, Title } from "@mantine/core";
import { memo, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { FaBirthdayCake, FaGift } from "react-icons/fa";
import { ApiGetAllUsers, ApiGetTotalAllUsers } from "../api/usuarios";
import BirthdayFireworksCanvas from "../components/BirthdayGlobos/BirthdayFireworksCanvas";

// 👇 Misma función para efecto confeti y BirthdayCard (omitido aquí por brevedad)
// Puedes copiarlo del componente anterior

const BirthdayPageTV = () => {
  const [todayUsers, setTodayUsers] = useState([]);
  const [monthUsers, setMonthUsers] = useState([]);
  const [upcomingUsers, setUpcomingUsers] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsers();

    // Slide automático cada 10 segundos
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % 3);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await ApiGetTotalAllUsers();
      if (res.sms === "ok") {
        const totalPages = Math.ceil(res.data / 600);
        let all = [];
        for (let i = 1; i <= totalPages; i++) {
          const { data } = await ApiGetAllUsers({
            page: i,
            pageSize: 600,
            estado: true,
          });
          all = [...all, ...data];
        }

        const today = moment();
        const isToday = (d) => {
          const birthDate = moment(d, "DD/MM/YYYY");
          return (
            birthDate.date() === today.date() &&
            birthDate.month() === today.month()
          );
        };
        const isThisMonth = (d) =>
          moment(d, "DD/MM/YYYY").month() === today.month();
        const isUpcoming = (d) => {
          const birth = moment(d, "DD/MM/YYYY").set("year", today.year());
          return birth.isAfter(today) && birth.diff(today, "days") <= 7;
        };

        setTodayUsers(all.filter((u) => u.fecha_naci && isToday(u.fecha_naci)));
        setMonthUsers(
          all.filter((u) => u.fecha_naci && isThisMonth(u.fecha_naci))
        );
        setUpcomingUsers(
          all.filter((u) => u.fecha_naci && isUpcoming(u.fecha_naci))
        );
      }
    } catch (err) {
      setError("Error cargando datos de usuarios");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSlide = () => {
    if (slideIndex === 0) return { title: "🎂 Hoy", data: todayUsers };
    if (slideIndex === 1) return { title: "📅 Mes Actual", data: monthUsers };
    return { title: "🔜 Próximos 7 días", data: upcomingUsers };
  };

  const { title, data } = getCurrentSlide();

  const BirthdayCard = memo(({ user, isToday }) => {
    const birthDate = moment(user.fecha_naci, "DD/MM/YYYY");

    return (
      <motion.div
        key={user.usuario}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.03 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <Paper
          p="lg"
          style={{
            background: isToday
              ? "rgba(255, 255, 255, 0.95)"
              : "rgba(255, 255, 255, 0.9)",
            borderRadius: "15px",
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
            border: isToday ? "2px solid #ff6b6b" : "none",
          }}
        >
          <Group>
            <Avatar
              size="lg"
              color="pink"
              radius="xl"
              style={{ border: "2px solid #ff6b6b" }}
            >
              {user.nombres?.[0] || "?"}
            </Avatar>
            <div>
              <Text size="lg" weight={700} color="#2d3436">
                {user.nombres || "Nombre no disponible"} {user.apellidos}
                {isToday && (
                  <span style={{ color: "#ff6b6b", marginLeft: 8 }}>
                    🎉 HOY!
                  </span>
                )}
              </Text>
              <Group spacing="xs">
                <FaBirthdayCake color="#ff6b6b" />
                <Text color="#636e72">
                  {birthDate.isValid()
                    ? birthDate.format("DD [de] MMMM")
                    : "Fecha inválida"}
                </Text>
              </Group>
            </div>
          </Group>
        </Paper>
      </motion.div>
    );
  });

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Loader size="xl" variant="dots" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "2rem",
          color: "red",
          background: "#ffecec",
          borderRadius: "8px",
          margin: "2rem",
        }}
      >
        <Text size="xl" weight={700}>
          {error}
        </Text>
      </div>
    );
  }

  return (
    <>
      {slideIndex === 0 && todayUsers.length > 0 && (
        <BirthdayFireworksCanvas
          names={todayUsers.map((u) => u.nombres?.split(" ")[0] || "🎉")}
        />
      )}
      <Container
        fluid
        p="xl"
        style={{
          background: "linear-gradient(to right, #ff6b6b, #f06595)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          color: "white",
        }}
      >
        <Group position="center" mb="xl">
          <FaGift size={36} />
          <Title order={1} style={{ color: "white" }}>
            🎈 Cumpleañeros
          </Title>
          <FaGift size={36} />
        </Group>

        <AnimatePresence mode="wait">
          <motion.div
            key={slideIndex}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: "center" }}
          >
            <Title order={2} mb="lg">
              {title}
            </Title>
            <Group position="center" spacing="xl">
              {data.length === 0 ? (
                <Text>No hay cumpleañeros</Text>
              ) : (
                data.map((user) => (
                  <BirthdayCard user={user} isToday={slideIndex === 0} />
                ))
              )}
            </Group>
          </motion.div>
        </AnimatePresence>
      </Container>
    </>
  );
};

export default BirthdayPageTV;
